import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useNavigate} from 'react-router-dom';
import './Header.css';
import LoginModal from "../LoginModal/LoginModal";
import RegisterModal from "../RegisterModal/RegisterModal";

// Список адміністраторів
const ADMIN_EMAILS = ["rostyk.stup@gmail.com", "selezium.com@gmail.com"];

export default function Header({ user, setUser, getCookie, setShowCart }) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    // Перевіряємо чи користувач є адміністратором
    const isAdmin = user && ADMIN_EMAILS.includes(user.email);

    // ---------------------- Вибір мови ----------------------
    const [showLangMenu, setShowLangMenu] = useState(false);
    const langMenuRef = useRef(null);

    // Клік поза меню мов
    useEffect(() => {
        function handleClickOutside(e) {
            if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
                setShowLangMenu(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Функція перемикання мови
    const changeLanguage = (lang) => {
        i18n.changeLanguage(lang);
        setShowLangMenu(false);
    };

    // Визначити прапорець залежно від поточної мови
    const currentLang = i18n.language; // може бути 'ua' або 'en'
    const flagSrc = currentLang === 'en'
        ? 'https://thumbs.dreamstime.com/b/english-language-button-7315218.jpg'
        : 'https://vectorflags.s3.amazonaws.com/flags/ua-circle-01.png';

    // ---------------------- Перевірка cookie та user ----------------------
    useEffect(() => {
        const userData = getCookie('userData');
        if (userData && !user) {
            setUser(userData);
        }
    }, []); // Видалено dependencies щоб уникнути циклу, перевіряємо тільки один раз при монтуванні

    // ---------------------- Логін/реєстрація ----------------------
    const [showLogin, setShowLogin] = useState(false);
    const [showRegister, setShowRegister] = useState(false);

    const handleGoogleSuccess = async (idToken) => {
        console.log('Google ID Token: ', idToken);
        try {
            const res = await fetch(
                'https://5x5gu5f6ojvf4qvvmfg7wmysfu0vfdhq.lambda-url.eu-west-2.on.aws/auth',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'google',
                        token: idToken
                    })
                }
            );
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Google login error');

            // Успіх => зберегти userData у cookie
            document.cookie = `userData=${JSON.stringify({
                firstname: data.firstname,
                profile_image: data.profile_image,
                email: data.email,
                token: data.token
            })}; path=/`;

            // Перезапускаємо сторінку, щоб підтягнути оновлені дані
            window.location.reload();
        } catch (err) {
            alert('Google auth failed: ' + err.message);
        }
    };

    // ---------------------- Вихід (logout) ----------------------
    const deleteCookie = (name) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    };
    const handleLogout = () => {
        deleteCookie('userData');
        window.location.reload();
    };

    // ---------------------- Меню юзера ----------------------
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const userMenuRef = useRef(null);

    const toggleUserMenu = () => {
        setShowUserMenu((prev) => !prev);
    };

    useEffect(() => {
        function handleClickOutside(e) {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
                setShowUserMenu(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // ---------------------- Логіка приховування хедера при скролі ----------------------
    const lastScrollTopRef = useRef(0);
    const [hiddenHeader, setHiddenHeader] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
            const documentHeight = document.documentElement.scrollHeight;
            const windowHeight = window.innerHeight;
            
            // Перевіряємо, чи користувач знаходиться в низу сторінки (в межах 100px від низу)
            const isNearBottom = (currentScroll + windowHeight) >= (documentHeight - 100);
            
            // Перевіряємо напрямок скролу
            const isScrollingUp = currentScroll < lastScrollTopRef.current;
            const isScrollingDown = currentScroll > lastScrollTopRef.current;
            
            // Показуємо хедер якщо:
            // 1. Користувач в низу сторінки ЛБО
            // 2. Користувач скролить вгору ЛБО  
            // 3. Користувач в самому верху сторінки (в межах 10px)
            if (isNearBottom || isScrollingUp || currentScroll <= 10) {
                setHiddenHeader(false);
            } 
            // Ховаємо хедер тільки якщо користувач скролить вниз і не в низу сторінки
            else if (isScrollingDown && !isNearBottom && currentScroll > 100) {
                setHiddenHeader(true);
            }
            
            // Оновлюємо останню позицію скролу
            lastScrollTopRef.current = currentScroll <= 0 ? 0 : currentScroll;
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Знаходжу функцію, яка відповідає за навігацію в хедері
    const handleNavigation = (path) => {
        window.scrollTo(0, 0);
        navigate(path);
        setShowMobileMenu(false);
    };

    // ---------------------- JSX розмітка ----------------------
    return (
        <header className={`header-container ${hiddenHeader ? 'header-hidden' : ''}`}>
            {/* Лівий блок: логотип */}
            <Link to="/" className="header-left">
                <h1 className="header-logo">{t('header.logo')}</h1>
            </Link>

            {/* Центр: навігаційні посилання */}
            <nav className={`main-nav ${showMobileMenu ? 'mobile-active' : ''}`}>
                <ul>
                    <li>
                        <span onClick={() => handleNavigation('/category/laptop-cases')} className="nav-link">
                            {t('header.casesLaptop')}
                        </span>
                    </li>
                    <li>
                        <span onClick={() => handleNavigation('/category/iphone-cases')} className="nav-link">
                            {t('header.casesIphone')}
                        </span>
                    </li>
                    <li>
                        <span onClick={() => handleNavigation('/category/wallets')} className="nav-link">
                            {t('header.wallets')}
                        </span>
                    </li>
                </ul>
            </nav>

            {/* Права секція з елементами */}
            <div className="header-right">
                {/* Перемикач мови */}
                <div className="lang-switcher-wrapper" ref={langMenuRef}>
                    <button
                        className="lang-current"
                        onClick={() => setShowLangMenu(!showLangMenu)}
                    >
                        <img src={flagSrc} alt="Current Flag" className="lang-flag" />
                        <span className="caret">▼</span>
                    </button>

                    {showLangMenu && (
                        <div className="lang-dropdown">
                            <div className="lang-option" onClick={() => changeLanguage('ua')}>
                                <img
                                    src="https://vectorflags.s3.amazonaws.com/flags/ua-circle-01.png"
                                    alt="UA Flag"
                                    className="lang-flag"
                                />
                                <span>UA</span>
                            </div>
                            <div className="lang-option" onClick={() => changeLanguage('en')}>
                                <img
                                    src="https://thumbs.dreamstime.com/z/english-language-button-7315218.jpg"
                                    alt="EN Flag"
                                    className="lang-flag"
                                />
                                <span>EN</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Профіль користувача */}
                {user ? (
                    <div
                        className="header-link user-profile"
                        onClick={toggleUserMenu}
                        ref={userMenuRef}
                    >
                        <img
                            src="/images/user-icon.png"
                            alt="User Icon"
                            className="icon user-icon"
                        />
                        {showUserMenu && (
                            <div className="user-menu-dropdown">
                                {isAdmin && (
                                    <Link to="/admin" className="user-menu-item">
                                        Admin Panel
                                    </Link>
                                )}
                                <button onClick={handleLogout}>Logout</button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div
                        className="header-link user-profile"
                        onClick={() => setShowLogin(true)}
                    >
                        {t('header.login')}
                    </div>
                )}

                {/* Іконка кошика */}
                <div
                    className="header-icon"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setShowCart(true)}
                >
                    <img
                        src="https://cdn-icons-png.flaticon.com/512/3042/3042571.png"
                        alt="Cart Icon"
                        className="icon cart-icon"
                    />
                </div>

                {/* Гамбургер-меню для мобільних */}
                <div className="mobile-menu-toggle" onClick={() => setShowMobileMenu(!showMobileMenu)}>
                    <span className="hamburger-icon">&#9776;</span>
                </div>
            </div>

            {/* Модальні вікна */}
            <LoginModal
                visible={showLogin}
                onClose={() => setShowLogin(false)}
                onSwitchToRegister={() => {
                    setShowLogin(false);
                    setShowRegister(true);
                }}
                onGoogleSuccess={handleGoogleSuccess}
            />
            <RegisterModal
                visible={showRegister}
                onClose={() => setShowRegister(false)}
                onGoogleSuccess={handleGoogleSuccess}
            />
        </header>
    );
}
